import { useEffect, useMemo, useRef, useState, useContext } from "react";
import html2canvas from "html2canvas";

import DashboardLayout from "../../Layout/DashboardLayout/DashboardLayout";
import Filters from "../../components/Filters/Filters";
import KpiCard from "../../components/KpiCard/KpiCard";

import MediaTypesDonutChart from "../../components/Charts/MediaTypesDonutChart";
import BrazilStateMapChart from "../../components/Charts/BrazilStateMapChart";
import ClippingsPerDayLineChart from "../../components/Charts/ClippingsPerDayLineChart";
import QualitativePerDayLineChart from "../../components/Charts/QualitativePerDayLineChart";
import QualitativeDonutChart from "../../components/Charts/QualitativeDonutChart";
import TagsWordcloudChart from "../../components/Charts/TagsWordcloudChart";
import TagsDonutChart from "../../components/Charts/TagsDonutChart";
import TopSubjectsQualitativeChart from "../../components/Charts/TopSubjectsQualitativeChart";
import ProtagonismDonutChart from "../../components/Charts/ProtagonismDonutChart";
import ProtagonismBySubjectSankeyChart from "../../components/Charts/ProtagonismBySubjectSankeyChart";
import SourcesPerMarketingValueBarChart from "../../components/Charts/SourcesPerMarketingValueBarChart";
import SubjectsPerDayStackedAreaChart from "../../components/Charts/SubjectsPerDayStackedAreaChart";
import TopVehiclesQualitativeStackedBarChart from "../../components/Charts/TopVehiclesQualitativeStackedBarChart";
import NewsHighlightsPanel from "../../components/NewsHighlightsPanel/NewsHighlightsPanel";
import ReleaseOriginDonutChart from "../../components/Charts/ReleaseOriginDonutChart";
import ReleasePerDayLineChart from "../../components/Charts/ReleasePerDayLineChart";
import ReleasePerMediaTypeBarChart from "../../components/Charts/ReleasePerMediaTypeBarChart";
import ReleaseTopVehiclesBarChart from "../../components/Charts/ReleaseTopVehiclesBarChart";

import { formatBRL } from "../../utils/formatters";
import { useDashboardFilters } from "../../context/DashboardFiltersContext";
import { ExportModeContext } from "../../context/ExportModeContext";

import { fetchMediaHighlightsStreamlit } from "../../services/DataClipping.api";
import {
  mapPerDayToSeries,
  mapQualitativePerDayToSeries,
  mapTagsToWordcloud,
  mapTagsToTable,
  mapSubjectsQualitativeToStacked,
  mapProtagonismBySubjectToSankey,
  mapSubjectsPerDayToStackedSeries,
  mapTopVehiclesQualitativeToStackedBar,
} from "../../utils/dashboardSeries";

import {
  fetchDashboardTotalClippings,
  fetchDashboardTotalValuation,
  fetchDashboardTotalAudience,
  fetchDashboardMediaTypes,
  fetchDashboardClippingsByState,
  fetchDashboardClippingsPerDay,
  fetchDashboardQualitativeDistribution,
  fetchDashboardTags,
  fetchDashboardProtagonismDistribution,
  fetchDashboardProtagonismBySubject,
  fetchDashboardTotalVehicles,
  fetchDashboardSourcesPerMarketingValue,
  fetchDashboardSubjectsPerDay,
  fetchDashboardTopVehiclesQualitative,
  fetchDashboardOccupiedSpaceByMediaType,
  fetchDashboardReleaseOrigin,
  fetchDashboardReleasePerDay,
  fetchDashboardReleasePerMediaType,
  fetchDashboardReleaseTopVehicles,
} from "../../services/Dashboard.api";

import "./Dashboard.css";

function safeDate(v) {
  if (!v) return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;
  const dt = new Date(v);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toISODate(v) {
  const dt = safeDate(v);
  return dt ? dt.toISOString().slice(0, 10) : null;
}

function parseDateOnly(v) {
  if (!v) return null;

  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return new Date(v.getFullYear(), v.getMonth(), v.getDate());
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v).trim());
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const dt = safeDate(v);
  return dt ? new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()) : null;
}

function addMonthsClamped(date, months) {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = result.getDate();

  result.setDate(1);
  result.setMonth(result.getMonth() + months);

  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDayOfMonth));

  return result;
}

function isRangeLongerThanTwoMonths(startDate, endDate) {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);

  if (!start || !end || end < start) return false;
  return end > addMonthsClamped(start, 2);
}

function getDefaultTodayFilters() {
  const clientId = localStorage.getItem("clientId");
  const today = new Date();

  return {
    clientId,
    startDate: toISODate(today),
    endDate: toISODate(today),
    media: [],
    evaluation: "all",
    sentiment: null,
    category: null,
    subject: null,
    sources: [],
    selection: null,
    text: "",
  };
}

function SectionHeader({ kicker, title, description, right }) {
  return (
    <div className="dash-sec-head">
      <div className="dash-sec-head__left">
        {kicker ? <div className="dash-kicker">{kicker}</div> : null}

        <div className="dash-sec-head__texts">
          <div className="dash-sec-title-row">
            <h2 className="dash-sec-title">{title}</h2>
            <span className="dash-sec-glow" />
          </div>

          {description ? <p className="dash-sec-desc">{description}</p> : null}
        </div>
      </div>

      {right ? <div className="dash-sec-head__right">{right}</div> : null}
    </div>
  );
}

function sumNums(arr) {
  return Array.isArray(arr)
    ? arr.reduce((acc, v) => acc + (Number.isFinite(+v) ? +v : 0), 0)
    : 0;
}

function hasNonZeroSeries(arr) {
  return sumNums(arr) > 0;
}

function hasArrayData(arr) {
  return Array.isArray(arr) && arr.length > 0;
}

function hasPieData(arr) {
  return Array.isArray(arr) && arr.some((x) => Number(x?.y ?? 0) > 0);
}

function hasMapData(arr) {
  return Array.isArray(arr) && arr.some((it) => Number(it?.[1] ?? 0) > 0);
}

function hasStackedSeries(series) {
  if (!Array.isArray(series) || !series.length) return false;
  return series.some((s) => Array.isArray(s?.data) && sumNums(s.data) > 0);
}

function hasSankeyLinks(links) {
  return Array.isArray(links) && links.some((l) => Number(l?.value ?? 0) > 0);
}

function hasHighlights(raw) {
  return Array.isArray(raw) && raw.length > 0;
}

export default function Dashboard() {
  const pdfRef = useRef(null);
  const summaryExportRef = useRef(null);

  const { appliedFilters, setAppliedFilters } = useDashboardFilters();
  const [totalClippings, setTotalClippings] = useState(0);

  const [valuation, setValuation] = useState({
    total: 0,
    total_evaluated: 0,
    positive: 0,
    negative: 0,
    neutral: 0,
  });

  const [audience, setAudience] = useState({
    total: 0,
    total_evaluated: 0,
    positive: 0,
    negative: 0,
    neutral: 0,
  });

  const [mediaTypesData, setMediaTypesData] = useState([]);
  const [byStateData, setByStateData] = useState([]);
  const [perDayRaw, setPerDayRaw] = useState(null);
  const [qualDist, setQualDist] = useState([]);
  const [subjectsRaw, setSubjectsRaw] = useState(null);
  const [protagonismRaw, setProtagonismRaw] = useState(null);
  const [protBySubjectRaw, setProtBySubjectRaw] = useState(null);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [sourcesMarketingRaw, setSourcesMarketingRaw] = useState(null);
  const [subjectsPerDayRaw, setSubjectsPerDayRaw] = useState(null);
  const [topVehiclesQualRaw, setTopVehiclesQualRaw] = useState(null);
  const [highlightsRaw, setHighlightsRaw] = useState(null);
  const [occupiedSpaceRaw, setOccupiedSpaceRaw] = useState(null);
  const [releaseOriginRaw, setReleaseOriginRaw] = useState([]);
  const [releasePerDayRaw, setReleasePerDayRaw] = useState(null);
  const [releasePerMediaTypeRaw, setReleasePerMediaTypeRaw] = useState([]);
  const [releaseTopVehiclesRaw, setReleaseTopVehiclesRaw] = useState([]);
  const [exportingSummary, setExportingSummary] = useState(false);

  const exportMode = useContext(ExportModeContext);

  const [loading, setLoading] = useState({
    total: true,
    valuation: true,
    vehicles: true,
    audienceKpi: true,
    mediaTypes: true,
    byState: true,
    perDay: true,
    qualDist: true,
    subjects: true,
    protagonism: true,
    protBySubject: true,
    sourcesMarketing: true,
    subjectsPerDay: true,
    topVehiclesQual: true,
    highlights: true,
    occupiedSpace: true,
    releaseOrigin: true,
    releasePerDay: true,
    releasePerMediaType: true,
    releaseTopVehicles: true,
  });

  useEffect(() => {
    setAppliedFilters(getDefaultTodayFilters());
  }, [setAppliedFilters]);

  function handleApply(filtersPayload) {
    setAppliedFilters(filtersPayload);
  }

  async function handleExportSummaryImage() {
    if (!summaryExportRef.current || exportingSummary || isSummaryLoading) return;

    try {
      setExportingSummary(true);

      const canvas = await html2canvas(summaryExportRef.current, {
        backgroundColor: "#f3f4f6",
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const link = document.createElement("a");
      link.download = `dashboard-resumo-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Erro ao exportar resumo do dashboard:", error);
    } finally {
      setExportingSummary(false);
    }
  }

  useEffect(() => {
    if (!appliedFilters) return;
    let mounted = true;

    const setL = (key, value) => {
      if (!mounted) return;
      setLoading((prev) => ({ ...prev, [key]: value }));
    };

    const labelMap = {
      tv: "TV",
      radio: "Rádio",
      newspaper: "Jornal",
      web: "Web",
      site: "Web",
      unknown: "Outros",
    };

    setLoading({
      total: true,
      vehicles: true,
      valuation: true,
      audienceKpi: true,
      mediaTypes: true,
      byState: true,
      perDay: true,
      qualDist: true,
      subjects: true,
      protagonism: true,
      protBySubject: true,
      sourcesMarketing: true,
      subjectsPerDay: true,
      topVehiclesQual: true,
      highlights: true,
      occupiedSpace: true,
      releaseOrigin: true,
      releasePerDay: true,
      releasePerMediaType: true,
      releaseTopVehicles: true,
    });

    fetchDashboardTotalClippings(appliedFilters)
      .then((res) => {
        const total = res?.total ?? res?.count ?? res?.results?.total ?? 0;
        setTotalClippings(Number(total) || 0);
      })
      .catch(() => setTotalClippings(0))
      .finally(() => setL("total", false));

    fetchDashboardTotalVehicles(appliedFilters)
      .then((res) => {
        const total = res?.total_vehicles ?? res?.total ?? 0;
        setTotalVehicles(Number(total) || 0);
      })
      .catch(() => setTotalVehicles(0))
      .finally(() => setL("vehicles", false));

    fetchDashboardTotalValuation(appliedFilters)
      .then((res) => {
        setValuation({
          total: Number(res?.total ?? 0),
          total_evaluated: Number(res?.total_evaluated ?? 0),
          positive: Number(res?.positive ?? 0),
          negative: Number(res?.negative ?? 0),
          neutral: Number(res?.neutral ?? 0),
        });
      })
      .catch(() =>
        setValuation({
          total: 0,
          total_evaluated: 0,
          positive: 0,
          negative: 0,
          neutral: 0,
        })
      )
      .finally(() => setL("valuation", false));

    fetchDashboardTotalAudience(appliedFilters)
      .then((res) => {
        setAudience({
          total: Number(res?.total ?? 0),
          total_evaluated: Number(res?.total_evaluated ?? 0),
          positive: Number(res?.positive ?? 0),
          negative: Number(res?.negative ?? 0),
          neutral: Number(res?.neutral ?? 0),
        });
      })
      .catch(() =>
        setAudience({
          total: 0,
          total_evaluated: 0,
          positive: 0,
          negative: 0,
          neutral: 0,
        })
      )
      .finally(() => setL("audienceKpi", false));

    fetchDashboardMediaTypes(appliedFilters)
      .then((res) => {
        const obj = res || {};
        const seriesData = Object.entries(obj).map(([key, val]) => ({
          name: labelMap[key] || key,
          y: Number(val?.total ?? 0),
        }));
        setMediaTypesData(seriesData);
      })
      .catch(() => setMediaTypesData([]))
      .finally(() => setL("mediaTypes", false));

    fetchDashboardClippingsByState(appliedFilters)
      .then((res) => {
        const obj = res || {};
        const data = Object.entries(obj)
          .filter(([uf]) => uf && uf !== "Indefinido")
          .map(([uf, v]) => [
            `br-${String(uf).toLowerCase()}`,
            Number(v?.total ?? 0),
          ]);
        setByStateData(data);
      })
      .catch(() => setByStateData([]))
      .finally(() => setL("byState", false));

    fetchDashboardClippingsPerDay(appliedFilters)
      .then((res) => setPerDayRaw(res))
      .catch(() => setPerDayRaw(null))
      .finally(() => setL("perDay", false));

    fetchDashboardQualitativeDistribution(appliedFilters)
      .then((res) => setQualDist(res || []))
      .catch(() => setQualDist([]))
      .finally(() => setL("qualDist", false));

    fetchDashboardTags(appliedFilters)
      .then((res) => setSubjectsRaw(res))
      .catch(() => setSubjectsRaw(null))
      .finally(() => setL("subjects", false));

    fetchDashboardProtagonismDistribution(appliedFilters)
      .then((res) => setProtagonismRaw(res))
      .catch(() => setProtagonismRaw(null))
      .finally(() => setL("protagonism", false));

    fetchDashboardProtagonismBySubject(appliedFilters)
      .then((res) => setProtBySubjectRaw(res))
      .catch(() => setProtBySubjectRaw(null))
      .finally(() => setL("protBySubject", false));

    fetchDashboardSourcesPerMarketingValue(appliedFilters)
      .then((res) => setSourcesMarketingRaw(res || []))
      .catch(() => setSourcesMarketingRaw(null))
      .finally(() => setL("sourcesMarketing", false));

    fetchDashboardSubjectsPerDay(appliedFilters)
      .then((res) => setSubjectsPerDayRaw(res || null))
      .catch(() => setSubjectsPerDayRaw(null))
      .finally(() => setL("subjectsPerDay", false));

    fetchDashboardTopVehiclesQualitative(appliedFilters)
      .then((res) => setTopVehiclesQualRaw(res || []))
      .catch(() => setTopVehiclesQualRaw(null))
      .finally(() => setL("topVehiclesQual", false));

    fetchMediaHighlightsStreamlit(
      appliedFilters.clientId,
      appliedFilters.startDate,
      appliedFilters.endDate
    )
      .then((res) => setHighlightsRaw(Array.isArray(res) ? res : []))
      .catch(() => setHighlightsRaw([]))
      .finally(() => setL("highlights", false));

    fetchDashboardOccupiedSpaceByMediaType(appliedFilters)
      .then((res) => setOccupiedSpaceRaw(res || null))
      .catch(() => setOccupiedSpaceRaw(null))
      .finally(() => setL("occupiedSpace", false));

    fetchDashboardReleaseOrigin(appliedFilters)
      .then((res) => setReleaseOriginRaw(Array.isArray(res) ? res : []))
      .catch(() => setReleaseOriginRaw([]))
      .finally(() => setL("releaseOrigin", false));

    fetchDashboardReleasePerDay(appliedFilters)
      .then((res) => setReleasePerDayRaw(res || null))
      .catch(() => setReleasePerDayRaw(null))
      .finally(() => setL("releasePerDay", false));

    fetchDashboardReleasePerMediaType(appliedFilters)
      .then((res) => setReleasePerMediaTypeRaw(Array.isArray(res) ? res : []))
      .catch(() => setReleasePerMediaTypeRaw([]))
      .finally(() => setL("releasePerMediaType", false));

    fetchDashboardReleaseTopVehicles(appliedFilters)
      .then((res) => setReleaseTopVehiclesRaw(Array.isArray(res) ? res : []))
      .catch(() => setReleaseTopVehiclesRaw([]))
      .finally(() => setL("releaseTopVehicles", false));

    return () => {
      mounted = false;
    };
  }, [appliedFilters]);

  const anyLoading = useMemo(() => Object.values(loading).some(Boolean), [loading]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("dashboard-loading-change", {
        detail: { loading: anyLoading },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("dashboard-loading-change", {
          detail: { loading: false },
        })
      );
    };
  }, [anyLoading]);

  const noResults = useMemo(() => {
    if (anyLoading) return false;
    return Number(totalClippings || 0) === 0;
  }, [anyLoading, totalClippings]);

  const totalValue = useMemo(() => {
    return typeof totalClippings === "number"
      ? totalClippings.toLocaleString("pt-BR")
      : totalClippings ?? "—";
  }, [totalClippings]);

  const totalVehiclesValue = useMemo(() => {
    return typeof totalVehicles === "number"
      ? totalVehicles.toLocaleString("pt-BR")
      : totalVehicles ?? "—";
  }, [totalVehicles]);

  const valuationTotalValue = useMemo(() => {
    return formatBRL(valuation.total);
  }, [valuation.total]);

  const audienceTotalValue = useMemo(() => {
    return typeof audience.total === "number"
      ? audience.total.toLocaleString("pt-BR")
      : audience.total ?? "—";
  }, [audience.total]);

  const useMonthlyGranularity = useMemo(() => {
    return isRangeLongerThanTwoMonths(appliedFilters?.startDate, appliedFilters?.endDate);
  }, [appliedFilters?.startDate, appliedFilters?.endDate]);

  const periodUnitLabel = useMonthlyGranularity ? "mês" : "dia";
  const clippingsPeriodTitle = `Notícias por ${periodUnitLabel}`;
  const qualitativePeriodTitle = `Análise qualitativa por ${periodUnitLabel}`;
  const qualitativeExportTitle = `${clippingsPeriodTitle} (análise qualitativa)`;
  const releasePeriodTitle = `Releases por ${periodUnitLabel}`;
  const subjectsPeriodTitle = `Assuntos por ${periodUnitLabel}`;

  const perDaySimple = useMemo(() => {
    return mapPerDayToSeries(perDayRaw, { groupByMonth: useMonthlyGranularity });
  }, [perDayRaw, useMonthlyGranularity]);

  const perDayQualitative = useMemo(() => {
    return mapQualitativePerDayToSeries(perDayRaw, {
      groupByMonth: useMonthlyGranularity,
    });
  }, [perDayRaw, useMonthlyGranularity]);

  const qualitativeDonutData = useMemo(() => {
    const colorMap = {
      POSITIVA: "#16A34A",
      NEUTRA: "#F59E0B",
      NEGATIVA: "#EF4444",
    };

    return (qualDist || []).map((it) => ({
      name: it.value,
      y: Number(it.total || 0),
      color: colorMap[it.value] || "#9CA3AF",
    }));
  }, [qualDist]);

  const subjectsWordcloud = useMemo(() => mapTagsToWordcloud(subjectsRaw, 10), [subjectsRaw]);

  const tagsPieData = useMemo(() => {
    const arr = mapTagsToTable(subjectsRaw).slice(0, 10);
    const colors = [
      "#1F1533",
      "#2B1850",
      "#3A1B6E",
      "#4C1D95",
      "#5B21B6",
      "#6D28D9",
      "#7C3AED",
      "#8B5CF6",
      "#A78BFA",
      "#C4B5FD",
    ];

    return arr.map((it, idx) => ({
      name: it.subject,
      y: Number(it.total || 0),
      color: colors[idx % colors.length],
    }));
  }, [subjectsRaw]);

  const topSubjectsQual = useMemo(() => {
    return mapSubjectsQualitativeToStacked(subjectsRaw, 10);
  }, [subjectsRaw]);

  const protagonismDonutData = useMemo(() => {
    const arr = Array.isArray(protagonismRaw) ? protagonismRaw : [];
    const colorMap = {
      protagonista: "#111827",
      "menção relevante": "#6D28D9",
      "referência contextual": "#7C3AED",
      "referencia contextual": "#7C3AED",
      nulo: "#9CA3AF",
    };

    return arr
      .map((it) => {
        const name = String(it?.subject || "").trim();
        const y = Number(it?.total ?? 0);
        return { name, y, color: colorMap[name.toLowerCase()] || "#1F1533" };
      })
      .filter((x) => x.name && x.y > 0)
      .sort((a, b) => b.y - a.y);
  }, [protagonismRaw]);

  const protSankey = useMemo(() => {
    return mapProtagonismBySubjectToSankey(protBySubjectRaw, 10);
  }, [protBySubjectRaw]);

  const subjectsPerDay = useMemo(() => {
    return mapSubjectsPerDayToStackedSeries(subjectsPerDayRaw, 10, {
      groupByMonth: useMonthlyGranularity,
    });
  }, [subjectsPerDayRaw, useMonthlyGranularity]);

  const topVehiclesQual = useMemo(() => {
    return mapTopVehiclesQualitativeToStackedBar(topVehiclesQualRaw, 10);
  }, [topVehiclesQualRaw]);

  const occupiedSpaceCards = useMemo(() => {
    const obj = occupiedSpaceRaw && typeof occupiedSpaceRaw === "object" ? occupiedSpaceRaw : null;
    if (!obj) return [];

    const labelMap = {
      tv: "TV",
      radio: "Rádio",
      newspaper: "Jornal",
      web: "Web",
      site: "Web",
      unknown: "Outros",
    };

    return Object.entries(obj)
      .map(([key, v]) => {
        const total = Number(v?.total ?? 0);
        const isSeconds = key === "tv" || key === "radio";
        const metricLabel = isSeconds ? "Minutagem" : "Centimetragem";
        const unit = isSeconds ? "s" : "cm";

        return {
          key,
          title: `${metricLabel} • ${labelMap[key] || key}`,
          total,
          unit,
          value: `${total.toLocaleString("pt-BR")} ${unit}`,
        };
      })
      .filter((it) => Number.isFinite(it.total) && it.total > 0);
  }, [occupiedSpaceRaw]);

  const releaseOriginData = useMemo(() => {
    return (releaseOriginRaw || []).map((item) => ({
      name: item?.value || item?.name || "Indefinido",
      y: Number(item?.total || item?.y || 0),
    }));
  }, [releaseOriginRaw]);

  const releasePerDay = useMemo(() => {
    return mapPerDayToSeries(releasePerDayRaw, {
      groupByMonth: useMonthlyGranularity,
    });
  }, [releasePerDayRaw, useMonthlyGranularity]);

  const releasePerMediaTypeData = useMemo(() => {
    return Array.isArray(releasePerMediaTypeRaw) ? releasePerMediaTypeRaw : [];
  }, [releasePerMediaTypeRaw]);

  const releaseTopVehiclesData = useMemo(() => {
    return Array.isArray(releaseTopVehiclesRaw) ? releaseTopVehiclesRaw : [];
  }, [releaseTopVehiclesRaw]);

  const showKpiTotal = !noResults;
  const showKpiVehicles = !noResults && (loading.vehicles || totalVehicles > 0);
  const showKpiValuation = !noResults && (loading.valuation || Number(valuation.total || 0) > 0);
  const showKpiAudience = !noResults && (loading.audienceKpi || Number(audience.total || 0) > 0);

  const showOccupiedSpace = !noResults && (loading.occupiedSpace || occupiedSpaceCards.length > 0);

  const showMediaTypes = !noResults && (loading.mediaTypes || hasPieData(mediaTypesData));
  const showQualDonut = !noResults && (loading.qualDist || hasPieData(qualitativeDonutData));
  const showProtagonismDonut = !noResults && (loading.protagonism || hasPieData(protagonismDonutData));
  const showTagsDonut = !noResults && (loading.subjects || hasPieData(tagsPieData));

  const showClippingsPerDay =
    !noResults &&
    (loading.perDay ||
      (hasArrayData(perDaySimple.categories) && hasNonZeroSeries(perDaySimple.values)));

  const showQualPerDay =
    !noResults &&
    (loading.perDay ||
      (hasArrayData(perDayQualitative.categories) &&
        (hasNonZeroSeries(perDayQualitative.positive) ||
          hasNonZeroSeries(perDayQualitative.neutral) ||
          hasNonZeroSeries(perDayQualitative.negative))));

  const showByState = !noResults && (loading.byState || hasMapData(byStateData));
  const showSourcesMarketing =
    !noResults &&
    (loading.sourcesMarketing ||
      (Array.isArray(sourcesMarketingRaw) && sourcesMarketingRaw.length > 0));

  const showWordcloud =
    !noResults &&
    (loading.subjects || (Array.isArray(subjectsWordcloud) && subjectsWordcloud.length > 0));

  const showTopSubjectsQual =
    !noResults &&
    (loading.subjects ||
      (hasArrayData(topSubjectsQual.categories) &&
        (hasNonZeroSeries(topSubjectsQual.total) ||
          hasNonZeroSeries(topSubjectsQual.positive) ||
          hasNonZeroSeries(topSubjectsQual.neutral) ||
          hasNonZeroSeries(topSubjectsQual.negative))));

  const showSubjectsPerDay =
    !noResults &&
    (loading.subjectsPerDay ||
      (hasArrayData(subjectsPerDay.categories) && hasStackedSeries(subjectsPerDay.series)));

  const showProtSankey = !noResults && (loading.protBySubject || hasSankeyLinks(protSankey.links));

  const showTopVehicles =
    !noResults &&
    (loading.topVehiclesQual ||
      (hasArrayData(topVehiclesQual.categories) &&
        (hasNonZeroSeries(topVehiclesQual.positive) ||
          hasNonZeroSeries(topVehiclesQual.neutral) ||
          hasNonZeroSeries(topVehiclesQual.negative))));

  const showHighlights = !noResults && (loading.highlights || hasHighlights(highlightsRaw));

  const showReleaseOrigin = !noResults && (loading.releaseOrigin || hasPieData(releaseOriginData));

  const showReleasePerDay =
    !noResults &&
    (loading.releasePerDay ||
      (hasArrayData(releasePerDay.categories) && hasNonZeroSeries(releasePerDay.values)));

  const showReleasePerMediaType =
    !noResults &&
    (loading.releasePerMediaType ||
      (Array.isArray(releasePerMediaTypeData) &&
        releasePerMediaTypeData.some((item) => Number(item?.total || 0) > 0)));

  const showReleaseTopVehicles =
    !noResults &&
    (loading.releaseTopVehicles ||
      (Array.isArray(releaseTopVehiclesData) &&
        releaseTopVehiclesData.some((item) => Number(item?.total || 0) > 0)));

  const showSectionDistributions =
    showMediaTypes || showQualDonut || showProtagonismDonut || showTagsDonut;

  const showSectionTrends = showClippingsPerDay || showQualPerDay;

  const showSectionCoverage = showByState || showSourcesMarketing;

  const showSectionTopics = showWordcloud || showTopSubjectsQual || showSubjectsPerDay;

  const showSectionFlow = showProtSankey;

  const showSectionSources = showTopVehicles;

  const showSectionHighlights = showHighlights;

  const showSectionReleases =
    showReleaseOrigin ||
    showReleasePerDay ||
    showReleasePerMediaType ||
    showReleaseTopVehicles;

  const showAnySection =
    showSectionDistributions ||
    showSectionTrends ||
    showSectionCoverage ||
    showSectionTopics ||
    showSectionFlow ||
    showSectionSources ||
    showSectionHighlights ||
    showSectionReleases;

  const isSummaryLoading =
    loading.total ||
    loading.vehicles ||
    loading.valuation ||
    loading.audienceKpi ||
    loading.occupiedSpace;

  return (
    <DashboardLayout>
      <Filters value={appliedFilters} onApply={handleApply} />

      <div ref={pdfRef} id="pdf-dashboard">
        {noResults ? (
          <div
            style={{
              borderRadius: 14,
              padding: 18,
              border: "1px solid var(--border-2)",
              background: "var(--background-graphic)",
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
              marginTop: 14,
            }}
          >
            <div style={{ fontWeight: 900, fontSize: 14, color: "var(--text-color)" }}>
              Sem resultados para os filtros selecionados
            </div>
            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: "var(--text-2)" }}>
              Tente ajustar período, tipo de veículo, avaliação ou fontes para encontrar publicações.
            </div>
          </div>
        ) : null}

        {!noResults &&
        (showKpiTotal || showKpiVehicles || showKpiValuation || showKpiAudience || showOccupiedSpace) ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 14,
              marginBottom: 10,
            }}
          >
            <button
              type="button"
              onClick={handleExportSummaryImage}
              disabled={exportingSummary || isSummaryLoading}
              className="dashboard-export-summary-btn"
            >
              {isSummaryLoading
                ? "Carregando dados..."
                : exportingSummary
                ? "Exportando..."
                : "Exportar dados"}
            </button>
          </div>
        ) : null}

        <div ref={summaryExportRef}>
          {!noResults && (showKpiTotal || showKpiVehicles || showKpiValuation || showKpiAudience) ? (
            <div className="kpi-grid">
              {showKpiTotal ? (
                <KpiCard
                  title="Total de notícias"
                  value={totalValue}
                  subtitle=""
                  loading={loading.total}
                />
              ) : null}

              {showKpiVehicles ? (
                <KpiCard
                  title="Total de veículos"
                  value={totalVehiclesValue}
                  loading={loading.vehicles}
                />
              ) : null}

              {showKpiValuation ? (
                <KpiCard
                  title="Valoração total"
                  value={valuationTotalValue}
                  loading={loading.valuation}
                />
              ) : null}

              {showKpiAudience ? (
                <KpiCard
                  title="Audiência total"
                  value={audienceTotalValue}
                  loading={loading.audienceKpi}
                />
              ) : null}
            </div>
          ) : null}

          {showOccupiedSpace ? (
            <div className="kpi-grid">
              {occupiedSpaceCards.map((it) => (
                <KpiCard
                  key={it.key}
                  title={it.title}
                  value={it.value}
                  loading={loading.occupiedSpace}
                />
              ))}
            </div>
          ) : null}
        </div>

        {!noResults && !anyLoading && !showAnySection ? (
          <div
            style={{
              marginTop: 14,
              borderRadius: 14,
              padding: 18,
              border: "1px solid var(--border-2)",
              background: "var(--background-graphic)",
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
            }}
          >
            <div style={{ fontWeight: 900, fontSize: 14, color: "var(--text-color)" }}>
              Não há informações para exibir
            </div>
            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: "var(--text-2)" }}>
              Os serviços retornaram vazio para este recorte. Ajuste os filtros e tente novamente.
            </div>
          </div>
        ) : null}

        <div className="dashboard-sections">
          {showSectionDistributions ? (
            <section className="dash-section dash-section--glass">
              <SectionHeader
                kicker="Overview"
                title="Distribuições"
                description="Comparativos rápidos por veículo, qualitativo, protagonismo e tags."
              />

              <div className="dash-grid dash-grid--smart-4">
                {showMediaTypes ? (
                  <div className="dash-item" data-export-title="Tipos de veículos">
                    <MediaTypesDonutChart
                      data={mediaTypesData}
                      loading={loading.mediaTypes}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showQualDonut ? (
                  <div className="dash-item" data-export-title="Distribuição qualitativa">
                    <QualitativeDonutChart
                      title="Distribuição qualitativa"
                      data={qualitativeDonutData}
                      loading={loading.qualDist}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showProtagonismDonut ? (
                  <div className="dash-item" data-export-title="Protagonismo">
                    <ProtagonismDonutChart
                      title="Protagonismo"
                      data={protagonismDonutData}
                      loading={loading.protagonism}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showTagsDonut ? (
                  <div className="dash-item dash-item--full" data-export-title="Tags">
                    <TagsDonutChart
                      data={tagsPieData}
                      loading={loading.subjects}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {showSectionTrends ? (
            <section className="dash-section dash-section--glass">
              <SectionHeader
                kicker="Trends"
                title="Evolução no tempo"
                description="Linhas para leitura e comparação."
              />

              <div className="dash-grid dash-grid--2">
                {showClippingsPerDay ? (
                  <div
                    className="dash-item dash-item--full"
                    data-export-title={clippingsPeriodTitle}
                  >
                    <ClippingsPerDayLineChart
                      title={clippingsPeriodTitle}
                      categories={perDaySimple.categories}
                      values={perDaySimple.values}
                      loading={loading.perDay}
                      filename={useMonthlyGranularity ? "noticias-por-mes" : "noticias-por-dia"}
                      loadingLabel={`Carregando ${clippingsPeriodTitle.toLowerCase()}...`}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showQualPerDay ? (
                  <div
                    className="dash-item dash-item--full"
                    data-export-title={qualitativeExportTitle}
                  >
                    <QualitativePerDayLineChart
                      title={qualitativePeriodTitle}
                      categories={perDayQualitative.categories}
                      positive={perDayQualitative.positive}
                      neutral={perDayQualitative.neutral}
                      negative={perDayQualitative.negative}
                      loading={loading.perDay}
                      filename={
                        useMonthlyGranularity ? "qualitativo-por-mes" : "qualitativo-por-dia"
                      }
                      loadingLabel={`Carregando ${qualitativePeriodTitle.toLowerCase()}...`}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {showSectionReleases ? (
            <section className="dash-section dash-section--glass">
              <SectionHeader
                kicker="Releases"
                title="Análise de releases"
                description="Origem, evolução no tempo, tipo de veículos e top veículos."
              />

              <div className="dash-grid dash-grid--2">
                {showReleaseOrigin ? (
                  <div className="dash-item" data-export-title="Releases">
                    <ReleaseOriginDonutChart
                      title="Releases"
                      data={releaseOriginData}
                      loading={loading.releaseOrigin}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showReleasePerMediaType ? (
                  <div className="dash-item" data-export-title="Releases por tipo de veículo">
                    <ReleasePerMediaTypeBarChart
                      title="Releases por tipo de veículo"
                      data={releasePerMediaTypeData}
                      loading={loading.releasePerMediaType}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showReleasePerDay ? (
                  <div
                    className="dash-item dash-item--full"
                    data-export-title={releasePeriodTitle}
                  >
                    <ReleasePerDayLineChart
                      title={releasePeriodTitle}
                      categories={releasePerDay.categories}
                      values={releasePerDay.values}
                      loading={loading.releasePerDay}
                      filename={useMonthlyGranularity ? "releases-por-mes" : "releases-por-dia"}
                      loadingLabel={`Carregando ${releasePeriodTitle.toLowerCase()}...`}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showReleaseTopVehicles ? (
                  <div
                    className="dash-item dash-item--full"
                    data-export-title="Top 10 veículos com mais releases"
                  >
                    <ReleaseTopVehiclesBarChart
                      title="Top 10 veículos com mais releases"
                      data={releaseTopVehiclesData}
                      loading={loading.releaseTopVehicles}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {showSectionCoverage ? (
            <section className="dash-section dash-section--glass">
              <SectionHeader
                kicker="Coverage"
                title="Geografia e ranking"
                description="Mapa por UF e top veículos por valoração."
              />

              <div className="dash-grid dash-grid--2">
                {showByState ? (
                  <div className="dash-item" data-export-title="Notícias por estado">
                    <BrazilStateMapChart
                      data={byStateData}
                      loading={loading.byState}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showSourcesMarketing ? (
                  <div className="dash-item" data-export-title="Top veículos por valoração">
                    <SourcesPerMarketingValueBarChart
                      title="Top veículos por valoração"
                      data={sourcesMarketingRaw || []}
                      loading={loading.sourcesMarketing}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {showSectionTopics ? (
            <section className="dash-section dash-section--glass">
              <SectionHeader
                kicker="Topics"
                title="Assuntos"
                description="Top 10 assuntos em nuvem, qualitativo e evolução no período."
              />

              <div className="dash-grid dash-grid--2">
                {showWordcloud ? (
                  <div
                    className="dash-item dash-item--full"
                    data-export-title="Top 10 - Nuvem de assuntos"
                  >
                    <TagsWordcloudChart
                      data={subjectsWordcloud}
                      loading={loading.subjects}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showTopSubjectsQual ? (
                  <div
                    className="dash-item dash-item--full"
                    data-export-title="Top 10 assuntos (análise qualitativa)"
                  >
                    <TopSubjectsQualitativeChart
                      title="Top 10 assuntos por análise qualitativa"
                      categories={topSubjectsQual.categories}
                      positive={topSubjectsQual.positive}
                      neutral={topSubjectsQual.neutral}
                      negative={topSubjectsQual.negative}
                      total={topSubjectsQual.total}
                      sums={{
                        pos: topSubjectsQual.posSum,
                        neu: topSubjectsQual.neuSum,
                        neg: topSubjectsQual.negSum,
                        total: topSubjectsQual.totalsSum,
                      }}
                      loading={loading.subjects}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}

                {showSubjectsPerDay ? (
                  <div
                    className="dash-item dash-item--full"
                    data-export-title={subjectsPeriodTitle}
                  >
                    <SubjectsPerDayStackedAreaChart
                      title={subjectsPeriodTitle}
                      categories={subjectsPerDay.categories}
                      series={subjectsPerDay.series}
                      loading={loading.subjectsPerDay}
                      filename={useMonthlyGranularity ? "assuntos-por-mes" : "assuntos-por-dia"}
                      loadingLabel={`Carregando ${subjectsPeriodTitle.toLowerCase()}...`}
                      exportMode={exportMode}
                    />
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {showSectionFlow ? (
            <section className="dash-section dash-section--glass">
              <SectionHeader
                kicker="Flow"
                title="Protagonismo por assunto"
                description="Fluxo com área horizontal para leitura."
              />

              <div className="dash-grid dash-grid--2">
                <div className="dash-item dash-item--full" data-export-title="Protagonismo por assunto">
                  <ProtagonismBySubjectSankeyChart
                    title="Top 10 - Protagonismo por assunto"
                    links={protSankey.links}
                    loading={loading.protBySubject}
                    exportMode={exportMode}
                  />
                </div>
              </div>
            </section>
          ) : null}

          {showSectionSources ? (
            <section className="dash-section dash-section--glass">
              <SectionHeader
                kicker="Sources"
                title="Veículos"
                description="Top publicações por veículo no período."
              />

              <div className="dash-grid dash-grid--2">
                <div
                  className="dash-item dash-item--full"
                  data-export-title="Top 10 - Publicações por veículo"
                >
                  <TopVehiclesQualitativeStackedBarChart
                    title="Top 10 - Publicações por veículo"
                    categories={topVehiclesQual.categories}
                    positive={topVehiclesQual.positive}
                    neutral={topVehiclesQual.neutral}
                    negative={topVehiclesQual.negative}
                    loading={loading.topVehiclesQual}
                    exportMode={exportMode}
                  />
                </div>
              </div>
            </section>
          ) : null}

          {showSectionHighlights ? (
            <section className="dash-section dash-section--glass">
              <SectionHeader
                kicker="Highlights"
                title="Destaques"
                description="Notícias destaques."
              />

              <div className="dash-grid dash-grid--2">
                <div className="dash-item dash-item--full" data-export-title="Destaques">
                  <NewsHighlightsPanel
                    data={highlightsRaw}
                    loading={loading.highlights}
                    exportMode={exportMode}
                  />
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </DashboardLayout>
  );
}

